/**
 * CetusEd Admin Console — Active Alerts
 *
 * COMPLIANCE: Critical alerts (e.g., service degradation, data sync failure)
 *   must be acknowledged within the SLA window. Alerts related to PII access
 *   anomalies are flagged for immediate review under FERPA §1232g(b).
 */

import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAlerts, useAcknowledgeAlert } from '../../api/hooks';
import type { Alert } from '../../api/hooks';

export const Alerts: React.FC = () => {
  const { data: alerts, isLoading, error } = useAlerts();
  const acknowledgeAlert = useAcknowledgeAlert();

  const handleAcknowledge = useCallback(
    (alertId: string) => {
      // mutate() routes failures into acknowledgeAlert.isError, which is
      // rendered below — no swallowed rejection, no invisible failure.
      acknowledgeAlert.mutate(alertId);
    },
    [acknowledgeAlert],
  );

  if (isLoading) return <div className="admin-loading">Loading alerts...</div>;

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load alerts.
      </div>
    );
  }

  const unacknowledged = alerts?.filter((a) => !a.acknowledged) || [];
  const acknowledged = alerts?.filter((a) => a.acknowledged) || [];

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/monitoring">Monitoring</Link> / Alerts
      </nav>

      <div className="page-header">
        <h1>Alerts</h1>
        <span className="alert-count">{unacknowledged.length} unacknowledged</span>
      </div>

      {acknowledgeAlert.isError && (
        <div className="admin-error" role="alert">
          Failed to acknowledge alert. Please try again.
        </div>
      )}

      {alerts && alerts.length === 0 && (
        <p className="empty-state">No active alerts. System is operating normally.</p>
      )}

      {unacknowledged.length > 0 && (
        <section className="alert-section">
          <h2>Unacknowledged</h2>
          {unacknowledged.map((alert) => (
            <AlertCard key={alert.id} alert={alert} onAcknowledge={handleAcknowledge} />
          ))}
        </section>
      )}

      {acknowledged.length > 0 && (
        <section className="alert-section">
          <h2>Acknowledged</h2>
          {acknowledged.map((alert) => (
            <AlertCard key={alert.id} alert={alert} />
          ))}
        </section>
      )}
    </div>
  );
};

const AlertCard: React.FC<{ alert: Alert; onAcknowledge?: (id: string) => void }> = ({
  alert,
  onAcknowledge,
}) => {
  return (
    <div className={`alert-card alert-card--${alert.severity}`}>
      <div className="alert-header">
        <span className={`alert-severity alert-severity--${alert.severity}`}>
          {alert.severity.toUpperCase()}
        </span>
        <span className="alert-time">{new Date(alert.createdAt).toLocaleString()}</span>
      </div>
      <h3 className="alert-title">{alert.title}</h3>
      <p className="alert-message">{alert.message}</p>
      {alert.districtId && <p className="alert-district">District: {alert.districtId}</p>}
      <div className="alert-actions">
        {onAcknowledge && !alert.acknowledged && (
          <button
            className="admin-btn admin-btn--sm"
            onClick={() => onAcknowledge(alert.id)}
          >
            Acknowledge
          </button>
        )}
        {alert.acknowledged && alert.acknowledgedBy && (
          <span className="alert-acknowledged-by">
            Acknowledged by {alert.acknowledgedBy}
          </span>
        )}
      </div>
    </div>
  );
};
